import React, { useMemo } from 'react'
import { useTranslation } from '@structura/i18n'

// One row of the code-check documentation table. Fields mirror the backend
// CheckResult (PascalCase over the wire). Description (Forklaring) is populated
// server-side so it matches the PDF report.
interface CheckRow {
  checkName: string
  description: string
  formula: string
  values: string
  utilization: number
  skipped: boolean
}

interface CodeCheckDocTableProps {
  checks: unknown[] | undefined
}

function readString(check: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = check[key]
    if (typeof value === 'string' && value.length > 0) {
      return value
    }
  }
  return ''
}

function readNumber(check: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = check[key]
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }
  }
  return 0
}

// Collapse the merged ULS+SLS checks (one per combination) to one row per check,
// keeping the worst utilization, and sort most-critical first.
function buildRows(checks: unknown[]): CheckRow[] {
  const byName = new Map<string, CheckRow>()
  for (const raw of checks) {
    if (typeof raw !== 'object' || raw === null) {
      continue
    }
    const check = raw as Record<string, unknown>
    const checkName = readString(check, 'CheckName', 'checkName')
    if (checkName.length === 0) {
      continue
    }
    const utilization = readNumber(check, 'Utilization', 'utilization')
    const skipped = check.Skipped === true || check.skipped === true
    const valuesParts = [
      readString(check, 'SectionalForces', 'sectionalForces'),
      readString(check, 'CapacityValues', 'capacityValues'),
      readString(check, 'Details', 'details')
    ].filter(part => part.length > 0)
    const row: CheckRow = {
      checkName,
      description: readString(check, 'Description', 'description'),
      formula: readString(check, 'Formula', 'formula'),
      values: valuesParts.join(' · '),
      utilization,
      skipped
    }
    const existing = byName.get(checkName)
    // A real (non-skipped) check always wins over a skipped one; among real
    // checks the worst utilization wins.
    if (!existing || (!row.skipped && (existing.skipped || row.utilization > existing.utilization))) {
      byName.set(checkName, row)
    }
  }
  return Array.from(byName.values()).sort((a, b) => Number(a.skipped) - Number(b.skipped) || b.utilization - a.utilization)
}

const CodeCheckDocTable: React.FC<CodeCheckDocTableProps> = ({ checks }) => {
  const { t } = useTranslation()
  const rows = useMemo<CheckRow[]>(() => (Array.isArray(checks) ? buildRows(checks) : []), [checks])

  if (rows.length === 0) {
    return null
  }

  return (
    <div className="card mt-3">
      <div className="card-header">
        <h6 className="card-title mb-0">{t('beam.response.codeCheck.title')}</h6>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0" style={{ fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ width: '20%' }}>{t('beam.response.codeCheck.check')}</th>
                <th style={{ width: '46%' }}>{t('beam.response.codeCheck.explanation')}</th>
                <th style={{ width: '24%' }}>{t('beam.response.codeCheck.formula')}</th>
                <th style={{ width: '10%' }} className="text-end">{t('beam.response.codeCheck.ur')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.checkName}>
                  <td className="fw-semibold">{row.checkName}</td>
                  <td className="text-muted">{row.description || '—'}</td>
                  <td>
                    {row.formula && <div>{row.formula}</div>}
                    {row.values && <div className="text-muted" style={{ fontSize: '0.9em' }}>{row.values}</div>}
                    {!row.formula && !row.values && '—'}
                  </td>
                  <td className="text-end">
                    {row.skipped
                      ? <span className="badge bg-secondary">{t('beam.response.codeCheck.na')}</span>
                      : row.utilization >= 999.99
                        // Backend sentinel for "check could not be computed" (invalid input) — a
                        // literal 999.99 would read as a real ~1000× overutilization.
                        ? <span className="badge bg-danger" title={row.values || row.formula || undefined}>{t('beam.response.notComputable')}</span>
                        : <span className={`badge ${row.utilization <= 1.0 ? 'bg-success' : 'bg-danger'}`}>{row.utilization.toFixed(2)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default CodeCheckDocTable
